// Plan 08-04 / VISUAL-PASS — BoardLayer: 6×6 stone slab with inset cell wells.
//
// VisualDesign §6.1: a deep stone slab with inset cell wells — each cell is a
// recessed, rounded well (chess-square radius, §5.5) with a top-left inner
// shadow + bottom-right inner highlight so it reads as carved INTO the slab.
// Dark/light wells alternate (chess pattern). Colours derive from the static
// take-two theme tokens (surface stops) + the universal Inkwell/Bone tokens —
// never a hardcoded hex (check-token-usage). WS0b (ARCA-87) removed the runtime
// theme swap: surface stops now come straight from `themeTokens`.

import type { JSX } from 'react'
import { actionTokens, hexToInt, themeTokens } from '../../styles/tokens'
import type { BoardView } from '../board-view'
import type { PlayerView } from '../PlayerViewSchema'
import { resolveWordField } from '../wire-grids'
import {
  boardRows,
  CELL_SIZE_PX,
  CHESS_CELLS,
  cellIdxToScreen,
} from './board-layout'

interface BoardLayerProps {
  playerView: PlayerView
  /**
   * ARCA-749 (WS3) — which view's grid the background wells span. The chess view
   * is the FIXED 6×6 (36 cells) regardless of how far the word field has grown;
   * the letters view spans the growing word field (36 → 48 → 60). Defaults to
   * 'letters' so existing tests that mount BoardLayer in isolation keep their
   * grown-field assertions.
   */
  boardView?: BoardView
}

function hexToNumber(hex: string): number {
  try {
    return hexToInt(hex)
  } catch {
    return hexToInt(actionTokens.textPrimary)
  }
}

/** Scale a 0xRRGGBB int's channels by `f` (clamped). No hex literals. */
function scaleInt(c: number, f: number): number {
  const r = Math.min(255, Math.round(((c >> 16) & 0xff) * f))
  const g = Math.min(255, Math.round(((c >> 8) & 0xff) * f))
  const b = Math.min(255, Math.round((c & 0xff) * f))
  return (r << 16) | (g << 8) | b
}

/** Theme-derived palette for one board's cell wells. */
export interface CellWellColors {
  slab: number
  wellDark: number
  wellLight: number
  shadow: number
  highlight: number
}

export function cellWellColorsFor(
  surfaceDeep: string,
  surfaceMid: string,
): CellWellColors {
  const deep = hexToNumber(surfaceDeep)
  const mid = hexToNumber(surfaceMid)
  return {
    slab: scaleInt(deep, 0.62), // darker stone frame between wells
    wellDark: deep,
    wellLight: mid,
    shadow: hexToNumber(actionTokens.inkwell), // recessed top-left shadow
    // Folk reskin: textPrimary is now DARK ink, which would darken the
    // bottom-right edge instead of lighting it. Use the LIGHT onAction (white)
    // so the catch-light reads as a subtle warm highlight on the cream well.
    highlight: hexToNumber(actionTokens.onAction), // bottom-right catch-light
  }
}

/**
 * Draw one recessed, bevelled cell well into a Pixi Graphics at the local
 * origin (caller positions the Graphics at the cell's screen coords). Shared
 * with the /dev/pieces gallery backdrop so the recipe stays single-sourced.
 */
export function drawCellWell(
  g: {
    clear: () => unknown
    rect: (x: number, y: number, w: number, h: number) => unknown
    roundRect: (
      x: number,
      y: number,
      w: number,
      h: number,
      r: number,
    ) => unknown
    fill: (opt: number | { color: number; alpha?: number }) => unknown
  },
  isDark: boolean,
  c: CellWellColors,
): void {
  const S = CELL_SIZE_PX
  const inset = 3
  const r = 3
  const iw = S - 2 * inset
  g.clear()
  // Slab gap (the carved frame showing between wells).
  g.rect(0, 0, S, S)
  g.fill(c.slab)
  // The well itself.
  g.roundRect(inset, inset, iw, iw, r)
  g.fill(isDark ? c.wellDark : c.wellLight)
  // Recessed read: inner shadow at top + left, catch-light at bottom + right.
  g.rect(inset, inset, iw, 2)
  g.fill({ color: c.shadow, alpha: 0.32 })
  g.rect(inset, inset, 2, iw)
  g.fill({ color: c.shadow, alpha: 0.32 })
  g.rect(inset, S - inset - 2, iw, 2)
  g.fill({ color: c.highlight, alpha: 0.14 })
  g.rect(S - inset - 2, inset, 2, iw)
  g.fill({ color: c.highlight, alpha: 0.14 })
}

export function BoardLayer({
  playerView,
  boardView = 'letters',
}: BoardLayerProps): JSX.Element {
  const colors = cellWellColorsFor(
    themeTokens.surfaceDeep,
    themeTokens.surfaceMid,
  )

  // ARCA-749 (WS3) — the wells span the ACTIVE view's grid. Chess view is the
  // FIXED 6×6 (36 cells / 6 rows) and never grows; letters view spans the word
  // field, which grows 36 → 48 → 60 (T6 flop / T11 river). Sizing off the
  // active grid keeps the chess board square as the word field deepens.
  const cellCount =
    boardView === 'chess' ? CHESS_CELLS : resolveWordField(playerView).length
  const rows = boardRows(cellCount)
  const cells: JSX.Element[] = []
  for (let i = 0; i < cellCount; i++) {
    const { screen_x, screen_y } = cellIdxToScreen(
      i,
      playerView.your_slot,
      rows,
    )
    // Dark/light alternating — chess-board pattern. Even sum → dark, odd → light.
    const isDark = (screen_x + screen_y) % 2 === 0
    cells.push(
      <pixiGraphics
        key={`cell-${i}`}
        x={screen_x * CELL_SIZE_PX}
        y={screen_y * CELL_SIZE_PX}
        draw={(g) => drawCellWell(g, isDark, colors)}
      />,
    )
  }

  return <pixiContainer label="BoardLayer">{cells}</pixiContainer>
}
